/**
 * Sentinel value for "Other" / custom unit in the drug unit dropdown.
 * When selected, the form shows a text input for a custom unit.
 */
export const DRUG_UNIT_CUSTOM_VALUE = '__custom__'

/** Canonical ocular forms — require OD/OS/OU on prescriptions. */
export const OCULAR_DRUG_UNITS = ['eye drops', 'eye ointment', 'eye gel'] as const

/**
 * Legacy packaging still present on existing catalog rows.
 * Treated as ocular so eye-drop stock does not suddenly lock to N/A.
 * Removed from DRUG_UNIT_OPTIONS so new drugs pick precise forms.
 */
export const LEGACY_OCULAR_DRUG_UNITS = ['bottle', 'tube'] as const

const OCULAR_UNIT_SET = new Set<string>([
  ...OCULAR_DRUG_UNITS,
  ...LEGACY_OCULAR_DRUG_UNITS,
])

/**
 * Canonical drug unit options for dispensary inventory.
 * Units describe clinical form where it matters for prescription eye laterality.
 */
export const DRUG_UNIT_OPTIONS: string[] = [
  'eye drops',
  'eye ointment',
  'eye gel',
  'tablet',
  'capsule',
  'oral liquid',
  'box',
  'vial',
  'ampoule',
  'strip',
  'pack',
  'sachet',
  'ml',
  'g',
  'piece',
]

function normalizeDrugUnit(unit: string): string {
  return unit.trim().toLowerCase()
}

/** Whether prescriptions for this unit require OD/OS/OU (vs locked N/A). */
export function isOcularDrugUnit(unit: string): boolean {
  if (!unit) return false
  return OCULAR_UNIT_SET.has(normalizeDrugUnit(unit))
}

export type PrescriptionEyeDefault = 'OU' | 'N/A'

/** Default eye value when adding a prescription line for a drug with this unit. */
export function defaultEyeForDrugUnit(unit: string): PrescriptionEyeDefault {
  return isOcularDrugUnit(unit) ? 'OU' : 'N/A'
}
